import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	input,
	OnDestroy,
	OnInit,
	output,
	signal,
} from '@angular/core';
import {
	MatAccordion,
	MatExpansionPanel,
	MatExpansionPanelHeader,
	MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatButton, MatFabButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { City, Program, Slot } from '@/new-transport/_models/transport-map.model';
import { VehicleTransport } from '@/new-transport/_models/transport.model';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatIconModule } from '@angular/material/icon';
import { JsonPipe } from '@angular/common';
import { mergeMaps } from '@/_utils/merge-two-maps';
import { appendToMap } from '@/_utils/append-to-map';
import { calculateParticipantsAmountFromPrograms } from '@/new-transport/_utils/calculate-participants-amount-from-programs/calculate-participants-amount-from-programs';
import { programFactory } from '@/new-transport/_factory/program-factory';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';
import { Node, NodeType, ParticipantsTabAction, Programs } from './participants-tab.model';
import { calculateVehicleFreeSeats } from '@/new-transport/_utils/calculate-vehicle-free-seats/calculate-vehicle-free-seats';
import { TreeExpandedNodesService } from '@/new-transport/_shared/city-map/city/city-modal/participants-tab/tree-expanded-nodes/tree-expanded-nodes.service';
import { InfoboxService } from '@/common/infobox/infobox.service';

@Component({
	selector: 'app-participants-tab',
	standalone: true,
	imports: [
		MatAccordion,
		MatButton,
		MatExpansionPanel,
		MatExpansionPanelHeader,
		MatExpansionPanelTitle,
		MatTreeModule,
		MatIconModule,
		MatIconButton,
		JsonPipe,
		MatFabButton,
		MatMiniFabButton,
	],
	templateUrl: './participants-tab.component.html',
	styleUrl: './participants-tab.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantsTabComponent implements OnInit, OnDestroy {
	// IO
	readonly programs = input.required<Programs>();
	readonly buttonDisabled = input.required<boolean>();
	readonly city = input.required<City>();
	readonly action = input.required<ParticipantsTabAction>();
	readonly vehicle = input.required<VehicleTransport>();

	readonly updatePrograms = output<Programs>();

	programsArray = signal<Program[]>([]);

	readonly treeControl = new NestedTreeControl<Node>((node) => node.children);
	readonly dataSource = new MatTreeNestedDataSource<Node>();

	// enums
	readonly ParticipantsTabAction = ParticipantsTabAction;
	readonly NodeType = NodeType;

	private programsProperty: keyof Programs = 'outsideVehicle';

	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
	private readonly expandedNodes = inject(TreeExpandedNodesService);
	private readonly infoboxService = inject(InfoboxService);

	private readonly updateTreeData = rxMethod<Programs>(
		pipe(
			tap(() => {
				this.programsArray.set(
					this.action() === ParticipantsTabAction.TAKE
						? this.programs().outsideVehicle
						: this.programs().taken,
				);
				this.dataSource.data = this.mapProgramsToNodes(this.programsArray());
				this.treeControl.dataNodes = this.dataSource.data; // TODO: https://github.com/angular/components/issues/12469

				this.applyTreeNodesExpandedState();

				this.cdr.detectChanges();
			}),
		),
	);

	hasChild(_: number, node: Node): boolean {
		return !!node.children && node.children.length > 0;
	}

	ngOnInit(): void {
		this.programsProperty = this.action() === ParticipantsTabAction.TAKE ? 'taken' : 'outsideVehicle';
		this.updateTreeData(this.programs);
	}

	expandAll(): void {
		this.dataSource.data.forEach((node) => {
			this.treeControl.expandDescendants(node);
			this.expandedNodes.addNode(node.id, node.type);
		});
	}

	collapseAll(): void {
		this.dataSource.data.forEach((node) => {
			this.treeControl.collapseDescendants(node);
		});

		this.expandedNodes.removeAllNodes();
	}

	handleNodeClick(id: number, type: NodeType, action: ParticipantsTabAction): void {
		switch (type) {
			case NodeType.SLOT:
				this.handleSlot(id, action);
				break;
			case NodeType.RESERVATION:
				this.handleReservation(id, action);
				break;
			case NodeType.PROGRAM:
				this.handleProgram(id, action);
				break;
			default:
				const neverType: never = type;
				throw new Error(`There is no type like: ${neverType}`);
		}

		if (action === ParticipantsTabAction.LEAVE) {
			this.updatePrograms.emit({ taken: this.programsArray(), outsideVehicle: this.programs().outsideVehicle });
		} else if (action === ParticipantsTabAction.TAKE) {
			this.updatePrograms.emit({ taken: this.programs().taken, outsideVehicle: this.programsArray() });
		}
	}

	toggleNodeExpandedState(node: Node): void {
		if (this.expandedNodes.nodeIncluded(node.id, node.type)) {
			this.expandedNodes.removeNode(node.id, node.type);
		} else {
			this.expandedNodes.addNode(node.id, node.type);
		}
	}

	ngOnDestroy(): void {
		this.expandedNodes.removeAllNodes();
	}

	private handleSlot(id: number, action: ParticipantsTabAction): void {
		if (action === ParticipantsTabAction.TAKE && !this.hasFreeSeats(1)) {
			this.infoboxService.error('Brak wolnych miejsc w pojeździe');
			return;
		}
		this.programsArray().forEach((program) => {
			Array.from(program.reservation.entries()).forEach(([key, value]) => {
				// get clicked slot
				const slot = value.find((v) => v.id === id);
				if (!slot) {
					return;
				}
				const programAlreadyInAnotherTree = this.programs()[this.programsProperty].some(
					(v) => v.destinationId === program.destinationId,
				);
				// if program is already in other tree then add slots to it otherwise create new program
				if (programAlreadyInAnotherTree) {
					this.addSlotsToProgram(slot, program, key);
				} else {
					this.createNewProgram([slot], program, key);
				}
			});
		});

		this.removeSlotFromClickedProgram(id);
	}

	private handleReservation(id: number, action: ParticipantsTabAction): void {
		this.programsArray().forEach((program) => {
			const reservation = program.reservation.has(id);
			if (!reservation) {
				return;
			}

			if (action === ParticipantsTabAction.TAKE && !this.hasFreeSeats(program.reservation.get(id).length)) {
				this.infoboxService.error('Brak wolnych miejsc w pojeździe');
				return;
			}

			const programAlreadyInOtherTree = this.programs()[this.programsProperty].some(
				(v) => v.destinationId === program.destinationId,
			);

			if (programAlreadyInOtherTree) {
				this.addReservationToProgram(program, id);
			} else {
				this.createNewProgram(program.reservation.get(id), program, id);
			}
		});
		this.removeReservationFromClickedProgram(id);
	}

	private handleProgram(id: number, action: ParticipantsTabAction): void {
		const program = this.programsArray().find((program) => program.destinationId === id);

		if (
			action === ParticipantsTabAction.TAKE &&
			!this.hasFreeSeats(calculateParticipantsAmountFromPrograms([program]))
		) {
			this.infoboxService.error('Brak wolnych miejsc w pojeździe');
			return;
		}

		const programAlreadyInOtherTree = this.programs()[this.programsProperty].some(
			(v) => v.destinationId === program.destinationId,
		);
		if (programAlreadyInOtherTree) {
			this.copyDataFromProgramToAnotherProgram(program);
		} else {
			this.createNewProgram(program.reservation, program, id);
		}

		// remove clicked program
		const programsWithoutClickedProgram = this.programsArray().filter((program) => program.destinationId !== id);
		this.programsArray.set(programsWithoutClickedProgram);
	}

	private mapProgramsToNodes(programs: Program[]): Node[] {
		return programs.map((program) => {
			return {
				name: program.destinationName,
				id: program.destinationId,
				type: NodeType.PROGRAM,
				children: Array.from(program.reservation.entries()).map(([key, slots]) => ({
					name: `Rez. ${key}`,
					id: key,
					type: NodeType.RESERVATION,
					children: slots.map((slot) => ({
						id: slot.id,
						name: `${slot.userData ? `${slot.userData.firstName} ${slot.userData.lastName}` : 'Uczestnik bez danych'}`,
						type: NodeType.SLOT,
					})),
				})),
			};
		});
	}

	private hasFreeSeats(peopleToAdd: number): boolean {
		const freeSeats = calculateVehicleFreeSeats(
			this.vehicle().capacity,
			this.vehicle().crew,
			calculateParticipantsAmountFromPrograms(this.programs().taken) + peopleToAdd,
		);
		return freeSeats >= 0;
	}

	private createNewProgram(data: Slot[] | Map<number, Slot[]>, program: Program, id: number): void {
		const slotsMap = Array.isArray(data) ? new Map([[id, data]]) : data;
		this.programs()[this.programsProperty] = [
			...this.programs()[this.programsProperty],
			programFactory(program.destinationId, program.destinationName, slotsMap, program.fromCity),
		];
	}

	private addSlotsToProgram(slot: Slot, program: Program, id: number): void {
		this.programs()[this.programsProperty] = this.programs()[this.programsProperty].map(
			(programToAddSlot: Program) => {
				if (programToAddSlot.destinationId !== program.destinationId) {
					return programToAddSlot;
				}

				return {
					...programToAddSlot,
					reservation: appendToMap(programToAddSlot.reservation, id, slot),
				};
			},
		);
	}

	private removeSlotFromClickedProgram(id: number): void {
		const programsWithoutSlot = this.programsArray()
			.map((program) => {
				return {
					...program,
					reservation: new Map(
						Array.from(program.reservation.entries()).map(([key, value]) => {
							return [key, value.filter((v) => v.id !== id)];
						}),
					),
				};
			})
			.map((program) => {
				//filter out empty reservations
				const newProgram = { ...program };
				newProgram.reservation = new Map(
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					Array.from(newProgram.reservation.entries()).filter(([_, value]) => value.length > 0),
				);
				return newProgram;
			})
			.filter((program) => {
				//filter out empty programs
				return program.reservation.size > 0;
			});

		this.programsArray.set(programsWithoutSlot);
	}

	private removeReservationFromClickedProgram(id: number): void {
		this.programsArray.set(
			this.programsArray()
				.map((program) => {
					program.reservation.delete(id);
					return program;
				})
				.filter((v) => v.reservation.size > 0),
		);
	}

	private addReservationToProgram(program: Program, id: number): void {
		this.programs()[this.programsProperty] = this.programs()[this.programsProperty].map(
			(programToAddReservation) => {
				if (programToAddReservation.destinationId !== program.destinationId) {
					return programToAddReservation;
				}

				// if there is already taken reservation, copy slots to it
				programToAddReservation.reservation.set(id, [
					...(programToAddReservation.reservation.get(id) ?? []),
					...program.reservation.get(id),
				]);
				return programToAddReservation;
			},
		);
	}

	private copyDataFromProgramToAnotherProgram(program: Program): void {
		this.programs()[this.programsProperty] = this.programs()[this.programsProperty].map((programToLeave) => {
			if (programToLeave.destinationId !== program.destinationId) {
				return programToLeave;
			}

			programToLeave.reservation = mergeMaps<number, Slot[]>(programToLeave.reservation, program.reservation);

			return programToLeave;
		});
	}

	private applyTreeNodesExpandedState(): void {
		this.treeControl.dataNodes
			.filter((node) => this.expandedNodes.nodeIncluded(node.id, node.type))
			.forEach((node) => {
				this.treeControl.expandDescendants(node);
			});
	}
}
